import { FormProvider, useForm } from "react-hook-form";
import { PaziaColorPicker, PaziaFormButton, PaziaLoader, PaziaSelect, PaziaSwitch } from "components/atoms";
import { HeadingStyles, shopApi, useHeadingStyles } from "api/shop";
import { useToaster } from "utils";
import { useEffect, useState } from "react";

const FONT_SIZE_OPTIONS = [
  { value: "text-base", label: "text-base" },
  { value: "text-lg", label: "text-lg" },
  { value: "text-xl", label: "text-xl" },
  { value: "text-2xl", label: "text-2xl" },
  { value: "text-3xl", label: "text-3xl" },
];

const FONT_WEIGHT_OPTIONS = [
  { value: "font-normal", label: "Normalna" },
  { value: "font-medium", label: "Średnia" },
  { value: "font-semibold", label: "Półpogrubiona" },
  { value: "font-bold", label: "Pogrubiona" },
];

const MARGIN_BOTTOM_OPTIONS = [
  { value: "mb-0", label: "mb-0" },
  { value: "mb-2", label: "mb-2" },
  { value: "mb-4", label: "mb-4" },
  { value: "mb-6", label: "mb-6" },
  { value: "mb-8", label: "mb-8" },
];

const ALIGNMENT_OPTIONS = [
  { value: "left", label: "Do lewej" },
  { value: "center", label: "Do środka" },
  { value: "right", label: "Do prawej" },
];

const DEFAULT_HEADING_STYLES: HeadingStyles = {
  fontSizeMobile: "text-2xl",
  fontSizeDesktop: "text-3xl",
  fontWeight: "font-semibold",
  color: "#0f172a",
  alignment: "left",
  marginBottom: "mb-8",
  showUnderline: false,
  underlineColor: "#00acbf",
};

const alignmentToTextClass = (alignment: HeadingStyles["alignment"]): string => {
  switch (alignment) {
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    case "left":
    default:
      return "text-left";
  }
};

export const ShopHeadingStylesForm = () => {
  const { headingStyles, isLoading, invalidate } = useHeadingStyles();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  // Backend returns a DTO with individually null fields when no config is saved.
  // Merge per-field so selects don't get `null` and end up with no selected option.
  const mergeWithDefaults = (
    incoming: Partial<HeadingStyles> | null | undefined,
  ): HeadingStyles => ({
    fontSizeMobile: incoming?.fontSizeMobile || DEFAULT_HEADING_STYLES.fontSizeMobile,
    fontSizeDesktop: incoming?.fontSizeDesktop || DEFAULT_HEADING_STYLES.fontSizeDesktop,
    fontWeight: incoming?.fontWeight || DEFAULT_HEADING_STYLES.fontWeight,
    color: incoming?.color || DEFAULT_HEADING_STYLES.color,
    alignment: incoming?.alignment || DEFAULT_HEADING_STYLES.alignment,
    marginBottom: incoming?.marginBottom || DEFAULT_HEADING_STYLES.marginBottom,
    showUnderline: incoming?.showUnderline ?? DEFAULT_HEADING_STYLES.showUnderline,
    underlineColor: incoming?.underlineColor || DEFAULT_HEADING_STYLES.underlineColor,
  });

  const methods = useForm<HeadingStyles>({
    defaultValues: mergeWithDefaults(headingStyles),
  });
  const { handleSubmit, reset, watch } = methods;

  useEffect(() => {
    if (headingStyles) {
      reset(mergeWithDefaults(headingStyles));
    }
  }, [headingStyles, reset]);

  const watched = watch();

  const onSubmit = async (data: HeadingStyles) => {
    setIsSaving(true);
    try {
      await shopApi.updateHeadingStyles(data);
      showSuccess("Zapisano styl nagłówków");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać stylu nagłówków");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  // Desktop size is stored without the `md:` prefix and applied at render-time.
  const previewClassName = [
    watched.fontSizeMobile,
    watched.fontSizeDesktop ? `md:${watched.fontSizeDesktop}` : "",
    watched.fontWeight,
    alignmentToTextClass(watched.alignment),
    watched.marginBottom,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-4xl gap-6">
        <div>
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Styl nagłówków</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Globalna konfiguracja stylu nagłówków na stronie produktu (course detail page)
          </p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <p className="mb-3 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Podgląd</p>
          {watched.showUnderline ? (
            <div className={`relative ${alignmentToTextClass(watched.alignment)}`}>
              <h2 className={`relative inline-block ${previewClassName}`} style={{ color: watched.color }}>
                Przykładowy nagłówek sekcji
                <span
                  className={`absolute -bottom-1 h-1 w-12 rounded-full ${
                    watched.alignment === "center"
                      ? "left-1/2 -translate-x-1/2"
                      : watched.alignment === "right"
                      ? "right-0"
                      : "left-0"
                  }`}
                  style={{ backgroundColor: watched.underlineColor }}
                />
              </h2>
            </div>
          ) : (
            <h2 className={previewClassName} style={{ color: watched.color }}>
              Przykładowy nagłówek sekcji
            </h2>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <PaziaSelect
            name="fontSizeMobile"
            label="Rozmiar (mobile)"
            options={FONT_SIZE_OPTIONS}
            isClearable={false}
            required
          />
          <PaziaSelect
            name="fontSizeDesktop"
            label="Rozmiar (desktop)"
            options={FONT_SIZE_OPTIONS}
            isClearable={false}
            required
          />
          <PaziaSelect
            name="fontWeight"
            label="Grubość"
            options={FONT_WEIGHT_OPTIONS}
            isClearable={false}
            required
          />
          <PaziaSelect
            name="marginBottom"
            label="Margines dolny"
            options={MARGIN_BOTTOM_OPTIONS}
            isClearable={false}
            required
          />
          <PaziaColorPicker name="color" label="Kolor" required />
          <PaziaSelect
            name="alignment"
            label="Wyrównanie"
            options={ALIGNMENT_OPTIONS}
            isClearable={false}
            required
          />
          <PaziaSwitch name="showUnderline" label="Pokaż podkreślenie" />
          {watched.showUnderline && (
            <PaziaColorPicker name="underlineColor" label="Kolor podkreślenia" required />
          )}
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};
